<template>
  <Dialog
    :value="dialog"
    :show-back-arrow="stepProfile !== 1"
    :buttons="dialogButtons"
    :title="
      stepProfile > 1 && stepData.title !== undefined ? stepData.title : $t('profile.titleMobile')
    "
    :show-title-in-content="stepProfile !== 1"
    :loading="mobileDialogLoading"
    :header-type="headerType"
    :left-header-icon="leftHeaderIcon"
    @clickBack="openSettings(stepData.previousStepType)"
    @input="closeModal"
  >
    <template #title>
      <v-img
        v-if="stepProfile === 1 && $auth.user.benefit.active && partnerData?.logoUrl"
        max-height="30"
        max-width="132"
        class="mx-auto"
        contain
        :src="partnerData?.logoUrl"
        :title="partnerData.name"
      />
    </template>
    <template #text>
      <template v-if="stepProfile === 1">
        <ProfileData />
        <MobileButtonGroup
          v-for="(buttonGroup, index) in buttons"
          :key="index"
          :buttons="buttonGroup"
          :class="{
            'mb-4': index !== buttons.length - 1
          }"
        />
        <v-btn color="tetrary" class="error--text mt-8" block @click="logout('login')">
          <v-icon size="24" color="error" class="mr-2">$logout</v-icon>
          {{ $t('profileSection.menu.logout') }}
        </v-btn>
      </template>
      <template v-else-if="stepData.cmpName">
        <component :is="stepData.cmpName" v-bind="stepData.params || null" />
      </template>
      <template v-else-if="stepData.content">
        <div v-html="stepData.content" />
      </template>
    </template>
  </Dialog>
</template>

<script>
import invitation from '@/mixins/invitation';
import accountSettings from '@/mixins/accountSettings';
import lodash from 'lodash';
import { mapGetters } from 'vuex';
import accountDelete from '@/mixins/accountDelete';
import Dialog from '@/components/Atoms/Dialog';
import ProfileData from '@/components/Atoms/ProfileData';
import MobileButtonGroup from '@/components/Atoms/MobileButtonGroup';

import AccountDelete from '@/components/Atoms/AccountDelete';
import BasicDataMobile from '@/components/Organisms/profile/BasicDataMobile';
import SecuritySettings from '@/components/Organisms/profile/SecuritySettings';
import PreventionSettings from '@/components/Organisms/profile/PreventionSettings';
import DocumentsMobile from '@/components/Organisms/profile/DocumentsMobile';
import DocumentMobile from '@/components/Organisms/profile/DocumentMobile';
import InvitationContent from '@/components/Molecules/InvitationContent';
import FeedbackContent from '@/feature/feedback/components/FeedbackContent.vue';
import { useFeedback } from '@/feature/feedback/composables/useFeedback.js';
import CustomerService from '@/components/Organisms/profile/CustomerService.vue';
import AboutApp from '@/components/Organisms/profile/AboutApp.vue';
import { Capacitor } from '@capacitor/core';

export default {
  name: 'AccountSettingsMobileDialog',
  components: {
    FeedbackContent,
    ProfileData,
    MobileButtonGroup,
    BasicDataMobile,
    Dialog,
    SecuritySettings,
    PreventionSettings,
    InvitationContent,
    DocumentsMobile,
    DocumentMobile,
    AccountDelete,
    CustomerService,
    AboutApp
  },
  mixins: [invitation, accountSettings, accountDelete],
  setup() {
    const { feedbackButtons, feedbackLoading, sendFeedback } = useFeedback();
    return { feedbackButtons, feedbackLoading, sendFeedback };
  },
  computed: {
    ...mapGetters({
      isValidUpdatePassword: 'forms/updatePassword/isValid',
      userDeactivated: 'profile/dashboard/userDeactivated',
      mobileDialogLoading: 'profile/accountSettings/mobileDialogLoading',
      deleted: 'profile/accountDelete/deleted'
    }),
    dialogButtons() {
      if (
        !(
          this.stepProfile === 2 &&
          ['basic', 'security', 'family', 'accountDelete', 'feedback'].includes(
            this.$route.query.type
          )
        )
      ) {
        return null;
      }

      if (this.$route.query.type === 'family') {
        return [this.invitationMobileButtonSettings];
      }

      if (this.$route.query.type === 'accountDelete') {
        return this.accountDeleteButtons;
      }

      if (this.$route.query.type === 'feedback') {
        return this.feedbackButtons;
      }

      return [
        {
          type: 'sendButton',
          props: {
            loading: this.loading,
            'success-state': this.success,
            disabled: this.userDeactivated
          },
          events: {
            click: () => {
              this.saveSettings();
            }
          },
          text: this.$t('save')
        }
      ];
    },

    buttons() {
      let buttons = [
        [
          {
            img: 'user',
            text: this.$t('profile.basicData'),
            click: () => {
              this.openSettings('basic');
            }
          },
          {
            img: 'questionmark_circle',
            text: this.$t('dashboard.supportBox.title'),
            click: () => {
              this.openSettings('customerService');
            }
          }
        ]
      ];

      if (this.$auth.user.securitySection.show || this.isMobileApp) {
        buttons[0].push({
          img: 'lock',
          text: this.$t('profile.security'),
          click: () => {
            this.openSettings('security');
          }
        });
      }

      let secondButtons = [];
      if (this.$auth.user.services?.prevention === 'active') {
        secondButtons.push({
          img: 'prevention',
          text: this.$t('prevention.summary.title'),
          click: () => {
            this.openSettings('prevention');
          }
        });
      }

      if (this.showInvitation) {
        secondButtons.push({
          img: 'family',
          text: this.$t('profile.family'),
          click: () => {
            this.getShareLink();
            this.openSettings('family');
          }
        });
      }

      if (secondButtons.length) {
        buttons.push(secondButtons);
      }

      let addButtons = [
        {
          icon: '$fileUploaded',
          text: this.$t('legalDocuments'),
          click: () => {
            this.openSettings('documents');
          }
        }
      ];

      if (this.isMobileApp) {
        addButtons.push({
          icon: '$info',
          text: this.$t('profile.aboutApp'),
          click: () => {
            if (this.isFlutterReady) {
              window.flutter_inappwebview.callHandler('showAboutApp');
            } else if (Capacitor.isNativePlatform()) {
              this.openSettings('aboutApp');
            }
          }
        });
      }

      addButtons.push({
        icon: '$bin',
        text: this.$t('profile.removeSection.title'),
        click: () => {
          this.openSettings('accountDelete');
        }
      });

      buttons.push(addButtons);

      return buttons;
    },
    dialog: {
      get() {
        return this.$store.state.profile.dashboard.dialog === 'profileMobile';
      },
      set(value) {
        this.$store.commit('profile/dashboard/setDialog', value);
      }
    },
    stepData() {
      switch (this.$route.query.type) {
        case 'basic':
          return {
            cmpName: 'BasicDataMobile',
            params: {
              'form-name': 'account-settings',
              'show-basic-data-title': false
            },
            title: this.$t('profile.basicData')
          };
        case 'security':
          return {
            cmpName: 'SecuritySettings',
            title: this.$t('profile.security')
          };
        case 'prevention':
          return {
            cmpName: 'PreventionSettings',
            title: this.$t('prevention.summary.title')
          };
        case 'family':
          return {
            cmpName: 'InvitationContent',
            title: this.$t('profile.family')
          };
        case 'documents':
          return {
            cmpName: 'DocumentsMobile',
            title: this.$t('legalDocuments')
          };
        case 'document':
          return {
            cmpName: 'DocumentMobile',
            title: '',
            previousStepType: 'documents'
          };
        case 'accountDelete':
          return {
            cmpName: 'AccountDelete',
            title: this.$t('profile.removeSection.title')
          };
        case 'feedback':
          return {
            cmpName: 'FeedbackContent',
            title: this.$t('feedback.menu')
          };
        case 'customerService':
          return {
            cmpName: 'CustomerService',
            title: this.$t('dashboard.supportBox.title')
          };
        case 'aboutApp':
          return {
            cmpName: 'AboutApp',
            title: this.$t('profile.aboutApp')
          };
        default:
          return null;
      }
    },
    stepProfile() {
      return !this.$route.query.stepProfile ||
        parseInt(this.$route.query.stepProfile) > 3 ||
        !this.stepData
        ? 1
        : +this.$route.query.stepProfile;
    },
    headerType() {
      if (['family'].includes(this.$route.query.type) || !this.$route.query.type) {
        return 'information';
      }
      return 'navigation';
    },
    leftHeaderIcon() {
      switch (this.$route.query.type) {
        case 'family':
          return 'icons/rodina.svg';
      }
      return null;
    }
  },
  watch: {
    async dialog(newVal) {
      if (newVal) {
        this.loading = false;
        this.success = false;
        await this.setAccountSettingsData();
      }
    },
    stepProfile() {
      this.dialogScrollTop();
    }
  },
  mounted() {
    window.closeDialog = () => {
      this.closeModal();
    };
  },
  beforeDestroy() {
    window.closeDialog = undefined;
  },
  methods: {
    openSettings(settingsType = null) {
      this.loading = false;
      this.success = false;
      if (settingsType) {
        const query = this.$route.query;
        delete query.documentName;
        this.$router.push(
          this.$localePath({
            name: this.currentPath,
            query: {
              ...query,
              stepProfile: '2',
              type: settingsType
            }
          })
        );
      } else {
        let query = {
          ...this.$route.query,
          stepProfile: undefined,
          type: undefined
        };
        this.$router.push(this.$localePath({ name: this.currentPath, query }));
      }
    },
    closeModal() {
      this.dialog = false;
    },
    async saveSettings() {
      this.loading = true;

      const userVerified = await this.checkUserVerification(false, true);

      this.logToFlutter({
        level: 'D',
        message: `SPA saveSettings userVerified => ${userVerified}`
      });

      if (!userVerified) {
        this.loading = false;
        return false;
      }

      switch (this.$route.query.type) {
        case 'basic':
          await this.submitBasicData(false);
          break;
        case 'security':
          if (this.newPassword && this.currentPassword) {
            this.success = !!(await this.updatePassword(true));
          }
          break;
      }

      this.loading = false;
    },
    async updateProfile() {
      if (!lodash.isEmpty(this.getBasicDataUpdateFields)) {
        this.loading = true;

        if (!(await this.checkUserVerification(false, true))) {
          this.loading = false;
          return false;
        }

        await this.updateAccountData(this.getBasicDataUpdateFields);
      } else {
        this.success = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.full_width {
  width: 100%;
}
</style>
